/**
 * Fills the mobile megamenu's deferred level-1 panels.
 *
 * snippets/menu-recursive-mobile.liquid renders each level-1 panel as an empty
 * placeholder carrying data-menu-handle + data-menu-pending; sections/mx-mobile-menu-panel.liquid
 * serves that category's levels 2-3. Rendering them inline cost 644 KB and 5,233 DOM
 * nodes on every page for a drawer most visitors never open.
 *
 * Deliberately does NOT touch MainMenu (assets/main.js:1062). Level 1 stays inline so
 * its constructor still finds .main-nav-mobile, and its click handling is delegated
 * there, so injected .js-toggle / .js-back elements work with no re-binding. This script
 * only fills panels; MainMenu still owns opening and closing them.
 */
(function () {
  const SECTION_ID = 'mx-mobile-menu-panel';

  // One in-flight or resolved promise per handle, so repeated taps fetch once.
  const cache = new Map();

  function rootUrl() {
    // Rendered server-side per request, so it is correct on every locale. Never
    // hardcode a market prefix: this store serves /en on dev and /en-en on production.
    let root = window.theme?.routes?.root || '/';
    if (!root.endsWith('/')) root += '/';
    return root;
  }

  function fetchPanel(handle) {
    if (!cache.has(handle)) {
      cache.set(
        handle,
        fetch(`${rootUrl()}collections/${encodeURIComponent(handle)}?sections=${SECTION_ID}`)
          .then((r) => {
            if (!r.ok) throw new Error(`HTTP ${r.status}`);
            return r.json();
          })
          .then((data) => data[SECTION_ID])
          .catch((e) => {
            // Drop the rejected promise so the next tap retries instead of replaying it.
            cache.delete(handle);
            throw e;
          })
      );
    }
    return cache.get(handle);
  }

  /**
   * Returns the panel's top-level elements, imported into this document.
   *
   * Every response is wrapped in <div id="shopify-section-mx-mobile-menu-panel">, so the
   * wrapper is discarded — injecting it would put 12 duplicate ids in the document.
   * Uses importNode rather than innerHTML: a repo hook blocks innerHTML assignment.
   *
   * Returns null only when the wrapper itself is missing (a malformed response). A
   * category with no panel yields [], which is a valid "nothing to add" result.
   */
  function parsePanelNodes(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const wrapper = doc.getElementById(`shopify-section-${SECTION_ID}`);
    if (!wrapper) return null;
    // children, not childNodes: an empty panel still carries a whitespace text node.
    return [...wrapper.children].map((el) => document.importNode(el, true));
  }

  function featuredLinks() {
    const raw = document.querySelector('main-menu')?.dataset.featuredLinks || '';
    return raw.split(',').map((s) => s.trim()).filter(Boolean);
  }

  // menu_featured_link belongs to the header section, which the separately-fetched panel
  // section cannot read, so the class is applied here rather than duplicating the setting
  // into a second schema.
  function applyFeaturedLinks(container, featured) {
    if (!featured.length) return;
    for (const link of container.querySelectorAll('a.main-nav__item')) {
      if (featured.includes(link.textContent.trim().toLowerCase())) {
        link.classList.add('main-nav__link--featured');
      }
    }
  }

  function settle(panel) {
    panel.removeAttribute('data-menu-pending');
    panel.removeAttribute('aria-busy');
  }

  function load(panel) {
    if (!panel.hasAttribute('data-menu-pending')) return;
    if (panel.dataset.menuLoading === '1') return;
    const handle = panel.dataset.menuHandle;
    if (!handle) return;

    panel.dataset.menuLoading = '1';
    const featured = featuredLinks();

    fetchPanel(handle)
      .then((html) => {
        const nodes = parsePanelNodes(html);
        if (!nodes) throw new Error('panel wrapper missing');

        // Drop the placeholder list (which holds the pre-rendered back control) only
        // once the real markup is in hand, so a failed fetch leaves the user a way back.
        panel.querySelector('.js-menu-panel-pending')?.remove();
        nodes.forEach((node) => panel.appendChild(node));
        applyFeaturedLinks(panel, featured);

        // An empty response means this category genuinely has no panel. Settle anyway,
        // so it is not refetched on every tap — empty is an answer, not a failure.
        settle(panel);
      })
      .catch(() => {
        // Leave data-menu-pending set so the next tap retries. The placeholder's back
        // button and the level-1 <a> both still work, so the menu stays usable.
      })
      .finally(() => {
        delete panel.dataset.menuLoading;
      });
  }

  // pointerdown fires before click, giving the fetch a head start on the 200ms slide.
  // click is bound too for keyboard activation of <summary>, which fires no pointer event.
  function onIntent(evt) {
    const summary = evt.target.closest('summary.js-toggle');
    if (!summary) return;
    const details = summary.closest('details');
    const panel = details?.querySelector(':scope > .main-nav__child[data-menu-pending]');
    if (panel) load(panel);
  }

  function init() {
    const mobileNav = document.querySelector('.main-nav-mobile');
    if (!mobileNav) return;
    mobileNav.addEventListener('pointerdown', onIntent, { capture: true, passive: true });
    mobileNav.addEventListener('click', onIntent, { capture: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
